import React, { type CSSProperties } from 'react'
import type { MenuOption } from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import { Avatar } from '../../Avatar'
import { Icon } from '../../Icon'
import * as styles from './styles'

const SEARCH_PLACEHOLDER = 'Type to search…'

type SelectedTokenProps = Readonly<{
  onRemove: () => void
  option: MenuOption
  isPending: boolean
}>

const SelectedToken: React.FC<SelectedTokenProps> = ({
  onRemove,
  option,
  isPending,
}) => (
  <div css={styles.token} data-pending={isPending}>
    <Avatar fullName={option.label} size={Spacing.M} src={option.avatarUrl} />
    <span css={styles.tokenLabel}>{option.label}</span>
    <button
      aria-label={`Remove ${option.label}`}
      css={styles.tokenRemove}
      onClick={onRemove}
      type="button"
    >
      <Icon ariaHidden glyph="gem-close" height={8} width={8} />
    </button>
  </div>
)
SelectedToken.displayName = 'Lapidary: SelectedToken ← TokenStrip'

type TokenStripProps<T extends MenuOption> = Readonly<{
  componentId: string
  onRemove: (item: T) => void
  onSearchBlur: React.FocusEventHandler
  onSearchFocus: React.FocusEventHandler
  onSearchChange: (term: string) => void
  onSearchKeyDown: React.KeyboardEventHandler
  pendingValues?: ReadonlySet<string>
  searchRef: React.RefObject<HTMLInputElement>
  searchTerm: string
  selectedItems: ReadonlyArray<T>
}>

/**
 * The user-mode menu header: the selected people are represented
 * as removable tokens with the search input flowing after them.
 */
export const TokenStrip = <T extends MenuOption>({
  componentId,
  onRemove,
  onSearchBlur,
  onSearchChange,
  onSearchFocus,
  onSearchKeyDown,
  pendingValues,
  searchRef,
  searchTerm,
  selectedItems,
}: TokenStripProps<T>) => (
  <div
    role="presentation"
    css={styles.root}
    // Stops a click on a token's remove button from blurring the search input,
    // which would close the menu before the click lands. The input itself
    // still needs the default to place a caret.
    onMouseDown={(e) => {
      if (e.target !== searchRef.current) {
        e.preventDefault()
      }
    }}
  >
    {selectedItems
      .map((item) => ({
        item,
        isPending: Boolean(pendingValues?.has(item.value)),
      }))
      // Notified people first, then pending ones
      .toSorted((a, b) => Number(a.isPending) - Number(b.isPending))
      .map(({ item, isPending }) => (
        <SelectedToken
          key={item.value}
          onRemove={() => onRemove(item)}
          option={item}
          isPending={isPending}
        />
      ))}
    <input
      aria-controls={`${componentId}-menu`}
      aria-label="Search options list"
      autoComplete="off"
      css={styles.input}
      id={`${componentId}-search`}
      onBlur={onSearchBlur}
      onChange={(e) => onSearchChange(e.target.value)}
      onFocus={onSearchFocus}
      onKeyDown={onSearchKeyDown}
      placeholder={SEARCH_PLACEHOLDER}
      ref={searchRef}
      style={
        {
          [styles.cssVariables.placeholderWidth]:
            `${SEARCH_PLACEHOLDER.length}ch`,
        } as CSSProperties
      }
      type="search"
      value={searchTerm}
    />
  </div>
)
TokenStrip.displayName = 'Lapidary: TokenStrip ← MultiCheckSelect'
